<template>
    <div
        v-if="expiry && canModify"
        class="form-col"
        @click.stop
        @touchstart.stop
    >
        <div class="flex items-center gap-2">
            <MuvalCheckbox
                v-model="draft.enabled"
                label="Add expiry reminder to company diary"
            />
        </div>
        <template v-if="draft.enabled">
            <p class="text-sm text-muval-gray-2">
                Expires {{ dayjs(expiry).format('D MMM YYYY') }} · Edit details in the company diary.
            </p>
            <div class="form-row">
                <div class="form-titled-item">
                    <span class="text-sm font-medium">Notify people</span
                    ><MuvalSelect
                        v-model="draft.recipient_user_ids"
                        mode="tags"
                        :select-options="peopleOptions"
                        value-prop="id"
                        display-label="name"
                        aria-label="Notify people"
                    />
                </div>
                <div class="form-titled-item">
                    <span class="text-sm font-medium">Notify roles</span
                    ><MuvalSelect
                        v-model="draft.recipient_roles"
                        mode="tags"
                        :select-options="roles"
                        value-prop="value"
                        display-label="label"
                        aria-label="Notify roles"
                    />
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted } from 'vue';
import { useForm } from 'vee-validate';
import MuvalSelect from '@inputs/MuvalSelect.vue';
import MuvalCheckbox from '@inputs/MuvalCheckbox.vue';
import { useUserStore } from '@store/user';
import { useCompanyDiaryPermissions } from '@composables/useCompanyDiaryPermissions';
import { useCompanyDiaryStaffOptions } from '@composables/useCompanyDiaryOptions';
import { SALES_ACTION_ROLE_LABELS } from '@composables/useSalesActions';
import dayjs from 'dayjs';
import { type DocumentExpiryDraft } from './documentExpiry';

useForm();
const props = withDefaults(defineProps<{ expiry: string; title?: string; files?: any[] }>(), {
    title: '',
    files: () => [],
});
const draft = defineModel<DocumentExpiryDraft>({ required: true });
const user = useUserStore();
const { canModify } = useCompanyDiaryPermissions();
const { staffOptions } = useCompanyDiaryStaffOptions();
onMounted(() => {
    if (
        !draft.value.recipients_initialized &&
        !draft.value.recipient_user_ids.length &&
        !draft.value.recipient_roles.length &&
        user.data?.id
    )
        draft.value.recipient_user_ids = [Number(user.data.id)];
    draft.value.recipients_initialized = true;
});
const peopleOptions = computed(() => [
    { id: Number(user.getUserId), name: 'Me' },
    ...staffOptions.value.filter((person) => Number(person.id) !== Number(user.getUserId)),
]);
const roles = Object.entries(SALES_ACTION_ROLE_LABELS).map(([value, label]) => ({ value, label }));
watch(
    () => props.title,
    (title, previous) => {
        if (!draft.value.title || draft.value.title === previous) draft.value.title = title;
    },
    { immediate: true },
);
watch(
    () => draft.value.enabled,
    (enabled) => {
        if (enabled && !draft.value.timezone) draft.value.timezone = user.getTimezone || '';
    },
);
</script>
